import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X, ArrowRight, Sun, Moon } from 'lucide-react';
import { Box, Flex, Link } from '@chakra-ui/react';
import BrandLockup from '../ui/BrandLockup';

import { useTheme } from '../../context/ThemeContext';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const handleLogoClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate('/');
  };

  return (
    <Box
      as="header"
      position="fixed"
      top="0"
      left="0"
      right="0"
      zIndex={1000}
      bg={isScrolled ? 'landing.surface' : 'transparent'}
      backdropFilter={isScrolled ? 'blur(20px)' : 'none'}
      opacity={isScrolled ? 0.95 : 1}
      borderBottomWidth={isScrolled ? '1px' : '0px'}
      borderColor="landing.border"
      transition="all 300ms ease"
      fontFamily="body"
    >
      <Flex
        maxW="1200px"
        mx="auto"
        px={{ base: 5, md: 8 }}
        h="72px"
        align="center"
        justify="space-between"
      >
        {/* Logo */}
        <Flex align="center" cursor="pointer" onClick={handleLogoClick} flexShrink={0}>
          <BrandLockup size={36} />
        </Flex>

        {/* Desktop Nav */}
        <Flex
          as="nav"
          role="navigation"
          align="center"
          gap={2}
          display={{ base: 'none', lg: 'flex' }}
        >
          {[
            ['features', 'Features'], 
            ['integrations', 'Integrations'], 
            ['pricing', 'Pricing']
          ].map(([id, label]) => (
            <Box
              key={id}
              as="button"
              px={4}
              py={2}
              borderRadius="xl"
              fontSize="sm"
              fontWeight="700"
              color="text.secondary"
              transition="all 200ms"
              _hover={{ color: 'brand.cyan', bg: 'brand.subtle' }}
              onClick={() => handleNavClick(id)}
            >
              {label}
            </Box>
          ))}
        </Flex>

        {/* Right actions */}
        <Flex align="center" gap={4}>
          <Flex
            as="button"
            onClick={toggleTheme}
            w="40px" h="40px"
            borderRadius="xl"
            bg="transparent"
            borderWidth="1px"
            borderColor="landing.border"
            align="center"
            justify="center"
            color="text.secondary"
            transition="all 200ms"
            cursor="pointer"
            _hover={{ color: 'brand.primary', bg: 'brand.subtle', borderColor: 'brand.subtle' }}
            aria-label="Toggle theme"
          >
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </Flex>

          <Box display={{ base: 'none', md: 'block' }}>
            <Link 
              href="/login" 
              fontSize="sm" 
              fontWeight="800" 
              color="text.main" 
              mr={4} 
              _hover={{ color: 'brand.cyan', textDecoration: 'none' }}
              onClick={(e) => { e.preventDefault(); navigate('/login'); }}
            >
              SIGN IN
            </Link>
            <Box
              as="button"
              px={6} py={2.5}
              borderRadius="xl"
              bg="brand.primary"
              color="white"
              fontWeight="800"
              fontSize="xs"
              letterSpacing="0.05em"
              border="none"
              cursor="pointer"
              transition="all 200ms"
              boxShadow="0 8px 20px rgba(27,79,204,0.3)"
              _hover={{ transform: 'translateY(-2px)', boxShadow: '0 12px 24px rgba(27,79,204,0.4)' }}
              onClick={() => navigate('/signup')}
            >
              GET STARTED <ArrowRight size={14} style={{ display: 'inline', marginLeft: '4px' }} />
            </Box>
          </Box>

          {/* Mobile menu toggle */}
          <Box
            as="button"
            display={{ base: 'flex', lg: 'none' }}
            w="40px" h="40px"
            borderRadius="xl"
            bg="brand.subtle"
            color="brand.primary"
            alignItems="center"
            justifyContent="center"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </Box>
        </Flex>
      </Flex>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <>
          {/* Backdrop — closes menu on tap, blocks interaction with page below */}
          <Box
            position="fixed"
            top="0"
            left="0"
            right="0"
            bottom="0"
            zIndex={999}
            bg="blackAlpha.600"
            backdropFilter="blur(2px)"
            onClick={() => setIsMobileMenuOpen(false)}
            display={{ lg: 'none' }}
          />
          <Box
            display={{ lg: 'none' }}
            position="relative"
            zIndex={1000}
            bg="landing.card"
            borderTopWidth="1px"
            borderColor="landing.border"
            px={5}
            py={8}
            boxShadow="2xl"
          >
            <Flex direction="column" gap={2} mb={6}>
              {[
                ['features', 'Features'],
                ['integrations', 'Integrations'],
                ['pricing', 'Pricing']
              ].map(([id, label]) => (
                <Box
                  key={id}
                  as="button"
                  px={4}
                  py={4}
                  borderRadius="xl"
                  fontSize="md"
                  fontWeight="800"
                  color="text.main"
                  textAlign="left"
                  w="full"
                  _hover={{ bg: 'brand.subtle', color: 'brand.cyan' }}
                  onClick={() => handleNavClick(id)}
                >
                  {label}
                </Box>
              ))}
            </Flex>
            <Box px={4} borderTopWidth="1px" borderColor="landing.border" pt={6}>
              <Box
                as="button"
                w="full"
                px={6} py={3.5}
                borderRadius="xl"
                bg="transparent"
                color="text.main"
                fontWeight="800"
                fontSize="md"
                border="1px solid"
                borderColor="landing.border"
                mb={3}
                onClick={() => { navigate('/login'); setIsMobileMenuOpen(false); }}
              >
                Sign In
              </Box>
              <Box
                as="button"
                w="full"
                px={6} py={4}
                borderRadius="xl"
                bg="brand.primary"
                color="white"
                fontWeight="800"
                fontSize="md"
                border="none"
                onClick={() => { navigate('/signup'); setIsMobileMenuOpen(false); }}
              >
                Get Started Free
              </Box>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default Header;
;
